import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { Checkpoint2StatusType, CheckpointAnswerLogType, CheckpointType } from '@fi-shared/checklist/checklist-enum';
import { AppTextService } from '@fi-shared/app-text';
import { ICheckpointAnswerLogModel, ICheckpointRange } from '@fi-shared/checklist/checklist-v2';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';

@Component({
    selector: 'fi-checklist2-answer-log',
    templateUrl: './answer-log.component.html',
    styleUrls: ['./answer-log.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-answer-log' },
    standalone: false,
})
export class FiChecklist2AnswerLogComponent implements OnInit {
    @Input() checkpointAnswerId: number;
    @Input() checkpointType: CheckpointType;
    @Input() checkpointRanges: ICheckpointRange[];
    @Input() measureUnitsText: string;

    isLogSectionExpanded = false;
    answerLogs: ICheckpointAnswerLogModel[] = [];

    @HostBinding('class.hidden') get isNewAnswer(): boolean {
        return !this.checkpointAnswerId;
    }

    constructor(
        private fiChecklist2Service: FiChecklist2Service,
        private fiChecklistHelperService: FiChecklistHelperService,
        private fiCheckpoint2HelperService: FiCheckpoint2HelperService,
        private appTextService: AppTextService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        //this.loadCheckpointAnswerLog(); // for tests
    }

    private isCheckpointAnswerLogLoaded = false;

    private loadCheckpointAnswerLog() {
        this.fiChecklist2Service
            .getCheckpointAnswerLog(this.checkpointAnswerId)
            .then((answerLogs: ICheckpointAnswerLogModel[]) => {
                this.answerLogs = answerLogs;
                this.cdr.markForCheck();
            });
    }

    toggleLogSectionExpansion() {
        this.isLogSectionExpanded = !this.isLogSectionExpanded;
        if (this.isLogSectionExpanded && !this.isCheckpointAnswerLogLoaded) {
            this.loadCheckpointAnswerLog();
            this.isCheckpointAnswerLogLoaded = true;
        }
    }

    isStatusCheckpointType(): boolean {
        return this.checkpointType === CheckpointType.Status;
    }

    getCheckpointLogLabelByType(answerLog: ICheckpointAnswerLogModel, checkpointType: CheckpointType): string {
        const logType = answerLog.AnswerLogType;
        switch (logType) {
            case CheckpointAnswerLogType.ReportingAsOk:
            case CheckpointAnswerLogType.ReportingAsNotOk:
                return checkpointType === CheckpointType.Status
                    ? this.appTextService.get('checklist.checkpoint_log_reported_as')
                    : this.appTextService.get('checklist.checkpoint_log_answered');
            case CheckpointAnswerLogType.ChangingReportedValue:
                return this.appTextService.get('checklist.checkpoint_log_changed_to');
            case CheckpointAnswerLogType.Commenting:
                return this.appTextService.get('checklist.checkpoint_log_added_comment');
            case CheckpointAnswerLogType.UpdatingComment:
                return this.appTextService.get('checklist.checkpoint_log_updated_comment');
            case CheckpointAnswerLogType.RemovingComment:
                return this.appTextService.get('checklist.checkpoint_log_removed_comment');
            case CheckpointAnswerLogType.AddingAttachment:
                return this.appTextService.get('checklist.checkpoint_log_added_attachment');
            case CheckpointAnswerLogType.RemovingAttachment:
                return this.appTextService.get('checklist.checkpoint_log_removed_attachment');
            case CheckpointAnswerLogType.ClearedCheckpoint:
                return this.appTextService.get('checklist.checkpoint_log_cleared_checkpoint');
            case CheckpointAnswerLogType.RemovingInvalidAttachment:
                return this.appTextService.get('checklist.checkpoint_log_removed_invalid_attachment');
        }
    }

    isAnswerLogType(logType: CheckpointAnswerLogType): boolean {
        return (
            logType === CheckpointAnswerLogType.ReportingAsOk ||
            logType === CheckpointAnswerLogType.ReportingAsNotOk ||
            logType === CheckpointAnswerLogType.ChangingReportedValue ||
            logType === CheckpointAnswerLogType.ClearedCheckpoint
        );
    }

    isCommentingLogType(logType: CheckpointAnswerLogType): boolean {
        return (
            logType === CheckpointAnswerLogType.Commenting ||
            logType === CheckpointAnswerLogType.UpdatingComment ||
            logType === CheckpointAnswerLogType.RemovingComment
        );
    }

    isAttachmentLogType(logType: CheckpointAnswerLogType): boolean {
        return (
            logType === CheckpointAnswerLogType.AddingAttachment ||
            logType === CheckpointAnswerLogType.RemovingAttachment
        );
    }

    isCheckpointAnswerValueShown(logType: CheckpointAnswerLogType) {
        return this.isAnswerLogType(logType) && !this.isStatusCheckpointType() && !this.isCheckpointCleared(logType);
    }

    isLogValueReportedAsOk(reportedValue: number): boolean {
        const checkpointStatus = this.fiCheckpoint2HelperService.getCheckpointStatus(
            reportedValue,
            this.checkpointRanges,
        );
        return checkpointStatus === Checkpoint2StatusType.Ok;
    }

    isLogValueReportedAsNotOk(reportedValue: number): boolean {
        const checkpointStatus = this.fiCheckpoint2HelperService.getCheckpointStatus(
            reportedValue,
            this.checkpointRanges,
        );
        return checkpointStatus === Checkpoint2StatusType.NotOk;
    }

    isCheckpointCleared(logType: CheckpointAnswerLogType): boolean {
        return logType === CheckpointAnswerLogType.ClearedCheckpoint;
    }

    getCommentContent(answerLog: ICheckpointAnswerLogModel) {
        return answerLog.Comments[0]?.Content;
    }

    protected readonly FiProfileSizes = FiProfileSizes;
    protected readonly FiItemsListOptionType = FiItemsListOptionType;
}
